interface NetworkInformation {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
}

interface NavigatorWithConnection extends Navigator {
  connection?: NetworkInformation;
}

export interface NetworkDiagnostics {
  online: boolean;
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
  visibilityState: DocumentVisibilityState;
  hasFocus: boolean;
  timeSinceNetworkChangeSec?: number;
  lastNetworkChangeType?: 'online' | 'offline';
}

let lastOnlineChange: number | undefined;
let lastOfflineChange: number | undefined;

// Register network change listeners
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    lastOnlineChange = Date.now();
  });
  window.addEventListener('offline', () => {
    lastOfflineChange = Date.now();
  });
}

export function getNetworkDiagnostics(): string {
  const connection = (navigator as NavigatorWithConnection).connection;
  const now = Date.now();

  // Calculate time since last network change
  let timeSinceNetworkChangeSec: number | undefined;
  let lastNetworkChangeType: 'online' | 'offline' | undefined;

  if (lastOnlineChange !== undefined || lastOfflineChange !== undefined) {
    if (lastOnlineChange !== undefined && lastOfflineChange !== undefined) {
      // Both have occurred, use the most recent
      if (lastOnlineChange > lastOfflineChange) {
        timeSinceNetworkChangeSec = Math.round((now - lastOnlineChange) / 1000);
        lastNetworkChangeType = 'online';
      } else {
        timeSinceNetworkChangeSec = Math.round((now - lastOfflineChange) / 1000);
        lastNetworkChangeType = 'offline';
      }
    } else if (lastOnlineChange !== undefined) {
      timeSinceNetworkChangeSec = Math.round((now - lastOnlineChange) / 1000);
      lastNetworkChangeType = 'online';
    } else if (lastOfflineChange !== undefined) {
      timeSinceNetworkChangeSec = Math.round((now - lastOfflineChange) / 1000);
      lastNetworkChangeType = 'offline';
    }
  }

  return formatNetworkDiagnostics({
    online: navigator.onLine,
    effectiveType: connection?.effectiveType,
    downlink: connection?.downlink,
    rtt: connection?.rtt,
    saveData: connection?.saveData,
    visibilityState: document.visibilityState,
    hasFocus: document.hasFocus(),
    timeSinceNetworkChangeSec,
    lastNetworkChangeType,
  });
}

function formatNetworkDiagnostics(diag: NetworkDiagnostics): string {
  const parts: string[] = [];

  parts.push(`online=${diag.online}`);
  parts.push(`visible=${diag.visibilityState}`);
  parts.push(`focus=${diag.hasFocus}`);

  // Network change info
  if (diag.timeSinceNetworkChangeSec !== undefined && diag.lastNetworkChangeType !== undefined) {
    parts.push(
      `networkChange=${diag.lastNetworkChangeType} ${diag.timeSinceNetworkChangeSec}s ago`,
    );
  } else {
    parts.push(`networkChange=none`);
  }

  // Connection API fields (Chrome/Android only)
  if (diag.effectiveType) {
    parts.push(`type=${diag.effectiveType}`);
  }
  if (diag.downlink !== undefined) {
    parts.push(`downlink=${diag.downlink}Mbps`);
  }
  if (diag.rtt !== undefined) {
    parts.push(`rtt=${diag.rtt}ms`);
  }
  if (diag.saveData !== undefined) {
    parts.push(`saveData=${diag.saveData}`);
  }

  return parts.join(', ');
}
